import { type LeadRequestStatus } from "@/entities/lead-claim-request";
import { type LinkV2 } from "@/entities/lead-link";
import { type IgnoreLevel } from "@/entities/lead/api/api.dto";
import { type Curator } from "@/entities/manager";
import { type UpdatedField } from "@/shared/types";
import { type User } from "@/entities/user";
import { WorkerV2 } from "@/entities/worker";

export type Lead = {
  id: string;
  note: string | boolean | null;
  telegramId: string;
  telegramNickname: string;
  telegramUsername: string;
  pushCount: number;
  pushWorkerCount: number;
  lastPush: string;
  isDead: boolean;
  isDeadHistory: UpdatedField<boolean>;
  depositsCount: number;
  ownersHistory: UpdatedField<{ ownerId: string | null }>;
  refsHistory: UpdatedField<{ refId: string | null }>;
  curatorsHistory: UpdatedField<{ curatorId: string | null }>;
  source: LeadSource;
  profit: number;
  status: {
    value: LeadStatus;
    lockHistory: {
      value: LeadStatus;
      createdAt: Date;
    }[];
    history: { value: LeadStatus; createdAt?: Date }[];
    isLocked: boolean;
    updatedAt: string;
  };
  ref: LinkV2 | null;
  owner: User["_id"] | User | null;
  ownerHistory: UpdatedField<{ ownerId: string }>;
  rejectionReason: string | null;
  irrelevance: UpdatedField<boolean>;
  ignore: {
    threshold: IgnoreLevel;
    history: UpdatedField<IgnoreLevel | "">;
    updatedAt: string;
  };
  curator: Curator;
  shares: LeadShares[];
  worker: WorkerV2 | null;
  survey: LeadSurvey;
  callDate: string;
  createdAt: string;
  updatedAt: string;
};

export type LeadIngoreLevel = "FIRST" | "SECOND" | "THIRD";

export type LeadShares = {
  amount: number;
  isActive: boolean;
  userId: string;
  username: string;
};

export type LeadSurvey = {
  hasAnybodyForVerification: boolean;
  hasDeposit: boolean;
  isAdult: boolean;
  isFamiliarWithCrypto: boolean;
  isSecondTryUsed: boolean;
};

export enum LeadStatus {
  REJECTED = "REJECTED",
  FRESH = "FRESH",
  WARMING_UP = "WARMING_UP",
  SURVEY_IN_PROGRESS = "SURVEY_IN_PROGRESS",
  SURVEY_COMPLETED = "SURVEY_COMPLETED",
  REMOVED_BY_SURVEY = "REMOVED_BY_SURVEY",
  TEXTED = "TEXTED",
  IGNORE = "IGNORE",
  HAD_CALL = "HAD_CALL",
  SPINNING = "SPINNING",
  LOOKING_FOR_DEPOSIT = "LOOKING_FOR_DEPOSIT",
  DEPOSIT = "DEPOSIT",
  UNLINKED = "UNLINKED",
}

export enum LeadSearchBy {
  OWNER_USERNAME = "ownerUsername",
  CURATOR_TG_USERNAME = "curatorTelegramUsername",
  TG_USERNAME = "telegramUsername",
  TG_ID = "telegramId",
  TG_NICKNAME = "telegramNickname",
  REF_NAME = "refName",
  WITHOUT_CURATOR = "withoutCurator",
}

export enum LeadSource {
  NEUTRAL = "NEUTRAL",
  VERTEX = "VERTEX",
  HELPER = "HELPER",
  NEXUS = "NEXUS",
  ASTREON = "ASTREON",
  P2P_WORK = "P2P_WORK",
  GLAVION = "GLAVION",
  WAVIUM = "WAVIUM",
  REMOTE_PRO = "REMOTE_PRO",
  AXYON = "AXYON",
}

export type ManagerLeadTable = {
  tgId: number;
  tgUsername: string;
  tgNickname: string;
  pushCount: number;
  pushWorkerCount: number;
  status: Lead["status"];
  isDead: boolean;
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  irrelevance: {
    value: boolean;
  };
  curator: {
    tgUsername: string;
  };
};

export type ModeratorLeadTable = {
  _id: string;
  leadTelegramId: string;
  leadTelegramUsername: string | null;
  user?: {
    username?: string;
  };
  curator?: {
    tgId: string;
  };
  leadLink?: {
    name: string;
  };
  attachments: string[];
  status: LeadRequestStatus;
  createdAt: Date;
};

export type HeadLeadTable = Lead;
